import Link from 'next/link'
import { useMemo, useState } from 'react'
import { PopupModal } from 'react-calendly'
import { twMerge } from 'tailwind-merge'
import { tv } from 'tailwind-variants'
import { z } from 'zod'

import { borderRadius } from '@nxt-insurance/ui-kit/styles/borderRadius'
import { bgColor, borderColor } from '@nxt-insurance/ui-kit/styles/colors'

import { borderRadiusEnum, colorEnum } from '@/cms/helperTypes'
import { webUrlSchema } from '@/cms/webUrl'
import { Icon } from '@/components/Icon'
import { EVENTS, LINKS_IDS } from '@/constants/analytics'
import { useAnalytics } from '@/context/AnalyticsContext'
import { useUrlFromWebUrlReference } from '@/hooks/useUrlFromWebUrlReference'
import { useWebUrlRel } from '@/hooks/useWebUrlRel'
import { getContrastColor } from '@/utils/style'
import type { PortableTextTypeComponentProps } from '@portabletext/react'

import { ConfigurableTypography, configurableTypography } from './ConfigurableTypography'

const linkStyles = tv({
  base: 'inline-flex cursor-pointer flex-row items-center gap-x-2.5 underline transition-opacity duration-300 ease-in-out hover:opacity-40',
})

const Chevron = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M9 5L16 12L9 19"
      stroke="#00140F"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export const LinkRendererSimple = (props: { text: string; url: string; sectionName: string }) => {
  const { track } = useAnalytics()
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false)
  if (props.url.includes('calendly.com')) {
    return (
      <>
        <PopupModal
          url={props.url}
          rootElement={
            (typeof window !== 'undefined'
              ? document.getElementById('__next')
              : null) as HTMLElement
          }
          onModalClose={() => {
            setIsCalendlyOpen(false)
          }}
          open={isCalendlyOpen}
        />

        <button className={linkStyles()} onClick={() => setIsCalendlyOpen(true)}>
          <span>{props.text}</span>
          <Chevron />
        </button>
      </>
    )
  }
  return (
    <Link
      className={linkStyles()}
      href={props.url}
      aria-label={props.text}
      onClick={() => {
        track(EVENTS.LinkClicked, {
          buttonId: LINKS_IDS.STYLED_LINK,
          sectionName: props.sectionName,
        })
      }}
    >
      <span>{props.text}</span>
      <Chevron />
    </Link>
  )
}

const linkSchema = z.discriminatedUnion('variant', [
  z.object({
    _type: z.literal('link'),
    _key: z.string(),
    variant: z.literal('bordered'),
    label: configurableTypography,
    borderColor: colorEnum,
    borderRadius: borderRadiusEnum,
    href: webUrlSchema,
  }),
  z.object({
    _type: z.literal('link'),
    _key: z.string(),
    variant: z.literal('filled'),
    label: configurableTypography,
    backgroundColor: colorEnum,
    borderRadius: borderRadiusEnum,
    href: webUrlSchema,
  }),
  z.object({
    _type: z.literal('link'),
    _key: z.string(),
    variant: z.literal('default'),
    label: configurableTypography,
    href: webUrlSchema,
  }),
])

export const LinkRenderer = (props: {
  node: PortableTextTypeComponentProps<unknown>
  sectionName: string
}) => {
  const { track } = useAnalytics()
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false)
  const link = useMemo(() => linkSchema.parse(props.node.value), [props.node.value])
  const { label, href, variant } = link
  const parsedHref = useUrlFromWebUrlReference(href)
  const value = useMemo(() => linkSchema.parse(props.node.value), [props.node.value])

  const rel = useWebUrlRel(href)

  const linkProps = useMemo(() => {
    switch (variant) {
      case 'bordered': {
        return {
          className: twMerge(
            'bg-transparent py-4 pr-2.5 pl-4',
            borderColor({ color: link.borderColor }),
            borderRadius({
              radius: link.borderRadius,
            })
          ),
        } as const
      }
      case 'filled': {
        return {
          className: twMerge(
            'py-4 pr-2.5 pl-4',
            borderColor({ color: link.backgroundColor }),
            bgColor({ color: link.backgroundColor }),
            borderRadius({
              radius: link.borderRadius,
            })
          ),
        } as const
      }
      default: {
        return {
          className: twMerge('border-transparent bg-transparent rounded-none p-0'),
        } as const
      }
    }
  }, [link, variant])
  if (parsedHref.includes('calendly.com')) {
    return (
      <>
        <PopupModal
          url={parsedHref}
          rootElement={
            (typeof window !== 'undefined'
              ? document.getElementById('__next')
              : null) as HTMLElement
          }
          onModalClose={() => {
            setIsCalendlyOpen(false)
          }}
          open={isCalendlyOpen}
        />
        <button
          className={twMerge(linkStyles(), linkProps.className)}
          onClick={() => setIsCalendlyOpen(true)}
        >
          <ConfigurableTypography
            node={{
              index: 0,
              isInline: false,
              renderNode: () => null,
              value: { _key: '1', ...label },
            }}
            align={label.textAlign ?? 'left'}
          />
          <Icon
            size="xsmall"
            icon={{
              name: getContrastColor(
                'backgroundColor' in link && link.backgroundColor ? link.backgroundColor : 'uiLight'
              ).includes('Light')
                ? 'action.chevronRightUiLight_16'
                : 'action.chevronRight_16',
            }}
          />
        </button>
      </>
    )
  }
  return (
    <Link
      className={twMerge(linkStyles(), linkProps.className)}
      href={parsedHref}
      aria-label={label.content}
      target={rel ? '_blank' : undefined}
      rel={rel}
      onClick={() => {
        track(EVENTS.LinkClicked, {
          buttonId: LINKS_IDS.STYLED_LINK,
          label: value.label.content,
          sectionName: props.sectionName,
        })
      }}
    >
      <ConfigurableTypography
        node={{
          index: 0,
          isInline: false,
          renderNode: () => null,
          value: { _key: '1', ...label },
        }}
        align={label.textAlign ?? 'left'}
      />
      <Icon
        size="xsmall"
        icon={{
          name: getContrastColor(
            'backgroundColor' in link && link.backgroundColor ? link.backgroundColor : 'uiLight'
          ).includes('Light')
            ? 'action.chevronRightUiLight_16'
            : 'action.chevronRight_16',
        }}
      />
    </Link>
  )
}
